import React, { useEffect, useRef } from 'react';

interface AudioWaveformProps {
  mediaRecorder: MediaRecorder | null;
  audioBlob: Blob | null;
  isRecording: boolean;
}

export const AudioWaveform: React.FC<AudioWaveformProps> = ({ mediaRecorder, audioBlob, isRecording }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const analyserRef = useRef<AnalyserNode>();
  const recordingDataRef = useRef<number[][]>([]);

  useEffect(() => {
    if (!canvasRef.current) return;
    
    let audioContext: AudioContext;
    let analyser: AnalyserNode;
    let bufferLength: number;
    let dataArray: Uint8Array;
    let source: AudioNode | undefined;
    let staticData: Uint8Array | undefined;

    const setupAudioAnalyser = async () => {
      audioContext = new AudioContext();
      analyser = audioContext.createAnalyser();
      analyserRef.current = analyser;
      analyser.fftSize = 256;
      bufferLength = analyser.frequencyBinCount;
      dataArray = new Uint8Array(bufferLength);

      try {
        if (isRecording && mediaRecorder) {
          // Live recording visualization
          source = audioContext.createMediaStreamSource(mediaRecorder.stream);
          source.connect(analyser);
          recordingDataRef.current = []; // Reset recording buffer
        } else if (audioBlob) {
          // Static waveform from recorded audio
          const arrayBuffer = await audioBlob.arrayBuffer();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          // Process the audio buffer to get peak frequency data
          const samples = audioBuffer.getChannelData(0);
          const blockSize = Math.floor(samples.length / 128);
          const frequencyData = new Uint8Array(128);
          
          for (let i = 0; i < 128; i++) {
            let peak = 0;
            for (let j = 0; j < blockSize; j++) {
              const amplitude = Math.abs(samples[i * blockSize + j]);
              peak = Math.max(peak, amplitude);
            }
            // Amplify the peak values and apply a minimum height
            frequencyData[i] = Math.max(50, Math.min(255, peak * 400));
          }
          
          // Store the static frequency data
          staticData = frequencyData;
        }
      } catch (error) {
        console.error('Error setting up audio analyser:', error);
      }
    };

    setupAudioAnalyser();

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext('2d')!;
    const width = canvas.width;
    const height = canvas.height;

    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);

      // Clear canvas with dark theme background
      canvasCtx.fillStyle = 'rgb(31, 41, 55)';
      canvasCtx.fillRect(0, 0, width, height);

      if (isRecording) {
        // Get new frequency data
        analyser.getByteFrequencyData(dataArray);
        
        // Add new data to recording buffer
        recordingDataRef.current.push(Array.from(dataArray));
        
        // Keep only last 50 frames
        if (recordingDataRef.current.length > 50) {
          recordingDataRef.current.shift();
        }

        // Draw scrolling waveform
        const barWidth = width / 50;
        recordingDataRef.current.forEach((frame, frameIndex) => {
          const x = frameIndex * barWidth;
          
          // Calculate average amplitude for this frame
          const avgAmplitude = frame.reduce((sum, val) => sum + val, 0) / frame.length;
          const barHeight = (avgAmplitude / 255) * height;
          
          // Create gradient
          const gradient = canvasCtx.createLinearGradient(x, height - barHeight, x, height);
          gradient.addColorStop(0, '#60A5FA');
          gradient.addColorStop(1, '#3B82F6');
          canvasCtx.fillStyle = gradient;
          
          // Draw bar
          canvasCtx.fillRect(x, height - barHeight, barWidth - 1, barHeight);
        });
      } else {
        // Draw static waveform
        const displayData = staticData || dataArray;
        const barWidth = width / displayData.length;
        
        for (let i = 0; i < displayData.length; i++) {
          // Apply non-linear scaling for better visual representation
        const normalizedValue = displayData[i] / 255;
        const barHeight = (Math.pow(normalizedValue, 0.7) * height);
          const x = i * barWidth;
          
          const gradient = canvasCtx.createLinearGradient(x, height - barHeight, x, height);
          gradient.addColorStop(0, '#60A5FA');
          gradient.addColorStop(1, '#3B82F6');
          canvasCtx.fillStyle = gradient;
          
          canvasCtx.fillRect(x, height - barHeight, barWidth - 1, barHeight);
        }
      }
    };

    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (source) {
        source.disconnect();
      }
      if (audioContext) {
        audioContext.close();
      }
    };
  }, [mediaRecorder, audioBlob, isRecording]);

  return (
    <canvas
      ref={canvasRef}
      width={300}
      height={50}
      className="w-full h-[50px] rounded-lg"
    />
  );
};
